/**
 * Bridges the Rust sync engine into the UI (Phase 3):
 * - re-registers engine tasks for enrolled cloud workspaces on login/start;
 * - on sync:rejected, marks the affected rows local-only and surfaces the
 *   server's own refusal message (services/syncRejections.js);
 * - on sync:remote-applied for the CURRENT workspace, re-hydrates the zustand
 *   stores from SQLite (same slices useCollectionsLoader's local path sets);
 * - keeps the latest per-workspace sync status in a ref for status UI.
 *
 * Mounted once from App.js. No-ops entirely outside tauri or when nothing is
 * enrolled.
 */
import { useEffect, useRef } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../utils/tauri';
import { SYNC_EVENTS, subscribeSyncEvent, loadDbSnapshot } from '../services/syncStore';
import { registerEnrolledCloudWorkspaces } from '../services/workspaceCloudMigration';
import { ensurePersonalSync } from '../services/personalSync';
import { personalScopeId } from '../services/personalScope';
import { ensureTeamBoardsSync } from '../services/teamBoardsSync';
import { ensureVaultSync } from '../services/vaultSync';
import { useTeamsStore } from '../store/teamsStore';
import { isSyncEngineWorkspace } from '../utils/workspaceStorage';
import { normalizeSnapshot } from '../services/localWorkspaceStore';
import { useWorkspaceStore } from '../store/workspaceStore';
import { useCollectionsStore } from '../store/collectionsStore';
import { useVariablesStore } from '../store/variablesStore';
import { useAuthStore } from '../store/authStore';
import { bumpDocumentsTree } from '../extensions/bundled/documents/documentsUiStore';
import { bumpBoards } from '../extensions/bundled/task-manager/boardsUiStore';
import { useSyncStatusStore } from '../store/syncStatusStore';
import { handleSyncRejected } from '../services/syncRejections';
import { onSyncStatus } from '../services/syncAuthRecovery';

export function useSyncEngine() {
  const statusRef = useRef(new Map()); // workspaceId -> last sync:status payload
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const teams = useTeamsStore((s) => s.teams);

  // Re-arm engine tasks for enrolled workspaces + the personal (notes/boards)
  // sync + a realtime task per team (team boards) whenever auth/workspaces/teams
  // settle.
  useEffect(() => {
    if (!isTauri() || !isAuthenticated) return;
    registerEnrolledCloudWorkspaces(workspaces, isAuthenticated);
    ensurePersonalSync();
    ensureTeamBoardsSync(teams);
    // Multi-device vault sync (PLAN.md §A2, call site 1): covers the
    // "already enrolled, needs its task re-armed after a restart" case for
    // the (currently rare, since unlock is session-only) case where the
    // vault happens to already be unlocked when auth settles. A no-op
    // whenever the vault is locked or unprotected.
    ensureVaultSync();
  }, [isAuthenticated, workspaces, teams]);

  // A2: drive the engine's app-wide "active" flag from the window lifecycle so
  // idle/hidden clients stop polling. Inactive on blur / hidden / 5 min without
  // input; active on focus / visible / any input. On reactivation the Rust side
  // wakes every task for an immediate catch-up pull, so returning shows fresh
  // data instantly. Writes still flush while paused (outbox notify path). All
  // guarded by isTauri — a no-op in the browser build.
  useEffect(() => {
    if (!isTauri()) return undefined;

    const IDLE_MS = 5 * 60 * 1000;
    let idleTimer = null;
    let current = null; // last value sent, to debounce redundant invokes

    const setActive = (active) => {
      if (active === current) return;
      current = active;
      invoke('sync_set_active', { active }).catch((err) =>
        console.warn('[sync] sync_set_active failed:', err),
      );
    };

    const goIdle = () => setActive(false);

    const markActive = () => {
      if (idleTimer) clearTimeout(idleTimer);
      setActive(true);
      idleTimer = setTimeout(goIdle, IDLE_MS);
    };

    const onVisibility = () => (document.hidden ? goIdle() : markActive());

    window.addEventListener('focus', markActive);
    window.addEventListener('blur', goIdle);
    document.addEventListener('visibilitychange', onVisibility);
    const inputEvents = ['mousemove', 'keydown', 'pointerdown'];
    inputEvents.forEach((e) => window.addEventListener(e, markActive, { passive: true }));

    markActive(); // arm the idle timer and report the current state

    return () => {
      if (idleTimer) clearTimeout(idleTimer);
      window.removeEventListener('focus', markActive);
      window.removeEventListener('blur', goIdle);
      document.removeEventListener('visibilitychange', onVisibility);
      inputEvents.forEach((e) => window.removeEventListener(e, markActive));
    };
  }, []);

  // Engine event subscriptions (mounted once).
  useEffect(() => {
    if (!isTauri()) return undefined;

    const unsubStatus = subscribeSyncEvent(SYNC_EVENTS.STATUS, (payload) => {
      if (payload?.workspaceId) statusRef.current.set(payload.workspaceId, payload);
      // COMMS-14: statusRef above is written but never read anywhere — mirror
      // every status into a reactive store so a UI element can actually
      // surface a broken sync instead of it going silently unnoticed.
      useSyncStatusStore.getState().setStatus(payload);
      // A 401 from the engine refreshes the token at once (syncAuthRecovery.js).
      onSyncStatus(payload);
    });

    // COMMS-12: the server-reported clock-skew warning was previously
    // computed and sent but deserialized nowhere on the client — wire it
    // into the same reactive store so SyncStatusIndicator can surface it.
    const unsubClockSkew = subscribeSyncEvent(SYNC_EVENTS.CLOCK_SKEW, (payload) => {
      useSyncStatusStore.getState().setClockSkew(payload);
    });

    // A push the server REFUSED (plan quota, permission). The engine has no
    // repair path for this — the outbox row is dropped, the local row keeps
    // the user's edit, and no pull redelivers it — so this handler is the
    // entire repair: it marks the row local-only and tells the user, in the
    // server's own words. See services/syncRejections.js.
    const unsubRejected = subscribeSyncEvent(SYNC_EVENTS.REJECTED, (payload) => {
      handleSyncRejected(payload);
    });

    const unsubApplied = subscribeSyncEvent(SYNC_EVENTS.REMOTE_APPLIED, async (payload) => {
      const wsId = String(payload?.workspaceId || '');

      // Personal scope (notes + boards): a change pulled from another device —
      // refresh the open Documents tree + Task Manager boards. Independent of
      // the active API workspace (the personal scope is never the current one).
      if (wsId === personalScopeId()) {
        bumpDocumentsTree();
        bumpBoards();
        return;
      }

      // Team scope (`team:<teamId>`): a change pulled from another member via
      // the team channel/pull — refresh the Task Manager boards grid + any
      // open team board, AND the Documents tree (team docs ride the same
      // scope/engine task as team boards — see teamBoardsSync). Not the
      // active API workspace, so nothing else to do.
      if (wsId.startsWith('team:')) {
        bumpBoards();
        bumpDocumentsTree();
        return;
      }

      const current = useWorkspaceStore.getState().currentWorkspace;
      const authed = useAuthStore.getState().isAuthenticated;
      if (!current || String(current.id) !== wsId) return;
      if (!isSyncEngineWorkspace(current, authed)) return;
      try {
        const snap = normalizeSnapshot(await loadDbSnapshot(String(current.id)));
        const collections = useCollectionsStore.getState();
        const variables = useVariablesStore.getState();
        collections.setCollections(snap.collections || []);
        collections.setSchemas(snap.schemas || { folders: [], items: [] });
        variables.setGlobalVariables(snap.globalVariables || []);
        variables.setWorkspaceVariables(snap.workspaceVariables || []);
        variables.setEnvironments(snap.environments || []);
      } catch (err) {
        console.warn('[sync] remote-applied re-hydration failed:', err);
      }
    });

    return () => {
      unsubStatus?.();
      unsubApplied?.();
      unsubClockSkew?.();
      unsubRejected?.();
    };
  }, []);

  return { statusRef };
}
